import React, { useRef, useCallback } from 'react';
import type { HSV } from './types';
import { hsvToRgb, rgbToHex } from '../../utils/colors';

interface ShadesBrightnessBarProps {
  shades: HSV[];
  activeIndex: number;
  onBrightnessChange: (index: number, value: number) => void;
  onActiveChange: (index: number) => void;
  onDragStart?: () => void;
  onDragEnd?: () => void;
}

const TRACK_H = 20;
const MIN_V = 5;
const MAX_V = 100;

const clamp = (v: number, min: number, max: number) =>
  Math.max(min, Math.min(max, v));

/** Convert brightness value (5–100) to CSS percentage for left offset. */
const vToPercent = (v: number): number =>
  ((v - MIN_V) / (MAX_V - MIN_V)) * 100;

const ShadesBrightnessBar: React.FC<ShadesBrightnessBarProps> = ({
  shades,
  activeIndex,
  onBrightnessChange,
  onActiveChange,
  onDragStart,
  onDragEnd,
}) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef<number | null>(null);

  /** Convert pointer clientX to brightness value. */
  const pointerToV = useCallback((clientX: number): number => {
    const el = trackRef.current;
    if (!el) return 50;
    const rect = el.getBoundingClientRect();
    const t = clamp((clientX - rect.left) / rect.width, 0, 1);
    return clamp(Math.round(MIN_V + t * (MAX_V - MIN_V)), MIN_V, MAX_V);
  }, []);

  /** Find the marker index whose pixel position is closest to clientX. */
  const hitTest = useCallback(
    (clientX: number): number => {
      const el = trackRef.current;
      if (!el || shades.length === 0) return 0;
      const rect = el.getBoundingClientRect();
      let closestIndex = 0;
      let closestDist = Infinity;
      for (let i = 0; i < shades.length; i++) {
        const pct = vToPercent(shades[i].v) / 100;
        const markerX = rect.left + pct * rect.width;
        const dist = Math.abs(clientX - markerX);
        if (dist < closestDist) {
          closestDist = dist;
          closestIndex = i;
        }
      }
      return closestIndex;
    },
    [shades],
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      const closestIndex = hitTest(e.clientX);
      draggingRef.current = closestIndex;
      onActiveChange(closestIndex);
      e.currentTarget.setPointerCapture(e.pointerId);
      onDragStart?.();
    },
    [hitTest, onActiveChange, onDragStart],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (draggingRef.current === null) return;
      const v = pointerToV(e.clientX);
      onBrightnessChange(draggingRef.current, v);
    },
    [pointerToV, onBrightnessChange],
  );

  const handlePointerEnd = useCallback(() => {
    draggingRef.current = null;
    onDragEnd?.();
  }, [onDragEnd]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (activeIndex < 0 || activeIndex >= shades.length) return;
      const currentV = shades[activeIndex].v;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        const newV = clamp(currentV - 1, MIN_V, MAX_V);
        onBrightnessChange(activeIndex, newV);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        const newV = clamp(currentV + 1, MIN_V, MAX_V);
        onBrightnessChange(activeIndex, newV);
      }
    },
    [activeIndex, shades, onBrightnessChange],
  );

  // Gradient: black → full brightness color
  const baseHsv = shades[0];
  const fullRgb = hsvToRgb(baseHsv.h, baseHsv.s, 100);
  const fullHex = rgbToHex(fullRgb.r, fullRgb.g, fullRgb.b);

  return (
    <div className="space-y-2">
      <div className="flex justify-between text-xs font-medium text-ink-secondary uppercase tracking-wider">
        <span>Shade Brightness</span>
        <span className="font-mono">
          {shades[activeIndex]?.v ?? 0}%
        </span>
      </div>

      {/* Horizontal track with color gradient and draggable markers */}
      <div
        ref={trackRef}
        tabIndex={0}
        className="relative touch-none select-none cursor-pointer"
        style={{ height: TRACK_H + 24, paddingTop: 12 - TRACK_H / 2 }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerEnd}
        onPointerCancel={handlePointerEnd}
        onKeyDown={handleKeyDown}
      >
        {/* Track background */}
        <div
          className="w-full rounded-lg"
          style={{
            height: TRACK_H,
            marginTop: 12 - TRACK_H / 2,
            background: `linear-gradient(to right, #000 0%, ${fullHex} 100%)`,
          }}
        />

        {/* Markers positioned on the track — purely visual, no event handlers */}
        {shades.map((shade, i) => {
          const pct = vToPercent(shade.v);
          const rgb = hsvToRgb(shade.h, shade.s, shade.v);
          const hex = rgbToHex(rgb.r, rgb.g, rgb.b);
          const isActive = i === activeIndex;
          const isBase = i === 0;
          const r = isBase ? 11 : 7;

          return (
            <div
              key={i}
              className={`
                absolute rounded-full border-2 pointer-events-none
                transition-shadow
                ${isActive ? 'ring-2 ring-indigo-400 ring-offset-1' : ''}
                ${isBase ? 'border-white shadow-md' : 'border-white/90 shadow-sm'}
              `}
              style={{
                width: r * 2,
                height: r * 2,
                backgroundColor: hex,
                left: `${pct}%`,
                top: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: isActive ? 20 : isBase ? 15 : 10,
              }}
              title={`${isBase ? 'B' : `S${i}`}: ${shade.v}%`}
            >
              {isBase && (
                <span
                  className="absolute text-[7px] font-bold text-white"
                  style={{
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                  }}
                >
                  B
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ShadesBrightnessBar;
